export function mountFormSelects(root) {
	root.querySelectorAll('.solufy-form-ui .solufy-field-box select:not([data-chat-country])').forEach((select) => {
		if (!select.dataset.enhanced) new FormSelect(select);
	});
}

class FormSelect {
	constructor(select) {
		this.select = select;
		this.field = select.closest('.solufy-field-box, .form-field');
		this.options = Array.from(select.options).filter(option => !option.disabled);
		this.active = 0;
		this.search = '';
		this.build();
		this.bind();
		this.sync();
		select.dataset.enhanced = 'true';
	}

	build() {
		const label = this.field.querySelector('.solufy-field-box__label, :scope > span');
		label.id = `${this.select.id}-label`;
		this.trigger = document.createElement('button');
		this.trigger.type = 'button';
		this.trigger.id = `${this.select.id}-trigger`;
		this.trigger.className = 'solufy-select-trigger';
		this.trigger.setAttribute('role', 'combobox');
		this.trigger.setAttribute('aria-haspopup', 'listbox');
		this.trigger.setAttribute('aria-expanded', 'false');
		this.trigger.setAttribute('aria-labelledby', `${label.id} ${this.trigger.id}`);
		this.trigger.setAttribute('aria-required', String(this.select.required));
		this.menu = document.createElement('div');
		this.menu.id = `${this.select.id}-options`;
		this.menu.className = 'solufy-select-menu';
		this.menu.setAttribute('role', 'listbox');
		this.menu.setAttribute('aria-labelledby', label.id);
		this.menu.hidden = true;
		this.trigger.setAttribute('aria-controls', this.menu.id);
		this.items = this.options.map((option, index) => {
			const item = document.createElement('div');
			item.id = `${this.select.id}-option-${index}`;
			item.className = 'solufy-select-option';
			item.setAttribute('role', 'option');
			item.textContent = option.textContent;
			item.addEventListener('pointerdown', event => event.preventDefault());
			item.addEventListener('click', () => this.choose(index));
			this.menu.append(item);
			return item;
		});
		document.body.append(this.menu);
		this.select.after(this.trigger);
		this.select.classList.add('solufy-select-native');
		this.select.tabIndex = -1;
		this.select.setAttribute('aria-hidden', 'true');
		this.field.htmlFor = this.trigger.id;
		this.field.classList.add('solufy-select-field');
	}

	bind() {
		this.trigger.addEventListener('click', event => {
			event.preventDefault();
			this.menu.hidden ? this.open() : this.close();
		});
		this.trigger.addEventListener('keydown', event => this.onKey(event));
		this.trigger.addEventListener('blur', () => this.close());
		this.select.addEventListener('change', () => this.sync());
		this.select.addEventListener('focus', () => this.trigger.focus());
		this.select.addEventListener('invalid', event => {
			event.preventDefault();
			this.trigger.setAttribute('aria-invalid', 'true');
			this.trigger.focus();
			this.open();
		});
		this.select.form?.addEventListener('reset', () => requestAnimationFrame(() => {
			this.sync();
			this.close();
		}));
		document.addEventListener('pointerdown', event => {
			if (!this.field.contains(event.target) && !this.menu.contains(event.target)) this.close();
		});
		window.addEventListener('resize', () => this.close());
		window.addEventListener('scroll', event => {
			if (!this.menu.contains(event.target)) this.close();
		}, true);
	}

	sync() {
		this.trigger.textContent = this.select.selectedOptions[0]?.textContent || 'Select an option';
		this.trigger.classList.toggle('is-placeholder', !this.select.value);
		this.trigger.removeAttribute('aria-invalid');
		this.items.forEach((item, index) => {
			item.setAttribute('aria-selected', String(this.options[index].value === this.select.value));
		});
	}

	open() {
		this.menu.hidden = false;
		this.trigger.setAttribute('aria-expanded', 'true');
		this.field.classList.add('is-open');
		const anchor = this.field.matches('.form-field') ? this.trigger : this.field;
		const rect = anchor.getBoundingClientRect();
		const below = window.innerHeight - rect.bottom - 12;
		const above = rect.top - 12;
		const upwards = below < 240 && above > below;
		this.menu.style.width = `${rect.width}px`;
		this.menu.style.left = `${rect.left}px`;
		this.menu.style.maxHeight = `${Math.min(300, upwards ? above : below)}px`;
		this.menu.style.top = `${upwards ? rect.top - this.menu.offsetHeight - 6 : rect.bottom + 6}px`;
		this.highlight(Math.max(0, this.options.findIndex(option => option.value === this.select.value)));
	}

	close() {
		this.menu.hidden = true;
		this.trigger.setAttribute('aria-expanded', 'false');
		this.trigger.removeAttribute('aria-activedescendant');
		this.field.classList.remove('is-open');
		this.search = '';
	}

	highlight(index) {
		this.active = (index + this.items.length) % this.items.length;
		this.items.forEach((item, position) => item.classList.toggle('is-active', position === this.active));
		this.trigger.setAttribute('aria-activedescendant', this.items[this.active].id);
		this.items[this.active].scrollIntoView({ block: 'nearest' });
	}

	choose(index) {
		this.select.value = this.options[index].value;
		this.select.dispatchEvent(new Event('input', { bubbles: true }));
		this.select.dispatchEvent(new Event('change', { bubbles: true }));
		this.close();
		this.trigger.focus();
	}

	onKey(event) {
		if (event.key === 'Tab' || event.key === 'Escape') {
			this.close();
			return;
		}
		if (['ArrowDown', 'ArrowUp', 'Home', 'End', 'Enter', ' '].includes(event.key)) {
			event.preventDefault();
			if (this.menu.hidden) { this.open(); return; }
			if (event.key === 'Enter' || event.key === ' ') this.choose(this.active);
			else if (event.key === 'Home') this.highlight(0);
			else if (event.key === 'End') this.highlight(this.items.length - 1);
			else this.highlight(this.active + (event.key === 'ArrowDown' ? 1 : -1));
		} else if (event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey) {
			event.preventDefault();
			if (this.menu.hidden) this.open();
			clearTimeout(this.searchTimer);
			this.search += event.key.toLowerCase();
			const index = this.options.findIndex(option => option.textContent.toLowerCase().startsWith(this.search));
			if (index >= 0) this.highlight(index);
			this.searchTimer = setTimeout(() => { this.search = ''; }, 600);
		}
	}
}
